import * as d3 from "d3";

export default class TreeRenderer {
  constructor(controller) {
    this.controller = controller;
    this.collapsedNodes = new Set(JSON.parse(localStorage.getItem("digitalTwinCollapsed") || "[]"));
  }

  render() {
    this.controller.mainGroup.selectAll("*").remove();
    this.controller.particleEngine.edgePaths.clear();

    const root = this.buildHierarchy();
    if (!root) return;

    const nodeSize = this.controller.isHorizontal
      ? [this.controller.nodeHeight + 15, this.controller.nodeWidth + this.controller.visibleEdgeHorizontal]
      : [this.controller.nodeWidth + 15, this.controller.nodeHeight + this.controller.visibleEdgeVertical];

    const treeLayout = d3
      .tree()
      .nodeSize(nodeSize)
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));

    const hierarchy = d3.hierarchy(root);
    treeLayout(hierarchy);

    if (this.controller.isHorizontal) {
      hierarchy.descendants().forEach((d) => {
        const temp = d.x;
        d.x = d.y;
        d.y = temp;
      });
    }

    hierarchy.descendants().forEach((d) => {
      if (d.data.type === "connector") {
        if (this.controller.isHorizontal) {
          const adjustment = this.controller.nodeWidth / 2 - 16;
          d.x -= adjustment;
        } else {
          const adjustment = this.controller.nodeHeight / 2 - 16;
          d.y -= adjustment;
        }
      }
    });

    this.offsetTreeForGridLine(hierarchy);
    this.renderGridLine(hierarchy);
    this.renderEdges(hierarchy);
    this.renderEdgeLabels(hierarchy);
    this.renderNodes(hierarchy);
  }

  buildHierarchy() {
    const nodeMap = new Map(this.controller.nodes.map((n) => [n.id, { ...n, children: [] }]));

    this.controller.nodes.forEach((node) => {
      if (node.parent) {
        const parent = nodeMap.get(node.parent);
        if (parent && !this.collapsedNodes.has(node.parent)) {
          parent.children.push(nodeMap.get(node.id));
        }
      }
    });

    const roots = this.controller.nodes.filter((n) => !n.parent);
    return roots.length > 0 ? nodeMap.get(roots[0].id) : null;
  }

  offsetTreeForGridLine(hierarchy) {
    const visibleEdge = this.controller.isHorizontal
      ? this.controller.visibleEdgeHorizontal
      : this.controller.visibleEdgeVertical;
    const offset = visibleEdge + 32;
    hierarchy.descendants().forEach((d) => {
      if (this.controller.isHorizontal) {
        d.x += offset;
      } else {
        d.y += offset;
      }
    });
  }

  renderGridLine(hierarchy) {
    const rootNode = hierarchy;
    if (!rootNode || rootNode.data.type !== "grid_connection") return;

    const gridGroup = this.controller.mainGroup.append("g").attr("class", "grid-line");
    const strokeWidth = this.getEdgeWidth(rootNode.data.max_power_kw);
    const circleRadius = 16;

    this.controller.gridNodeId = rootNode.data.id;

    const gridFlowKey = `grid_source->${rootNode.data.id}`;
    const reverseFlowKey = `${rootNode.data.id}->grid_source`;
    const isExporting = !!this.controller.actuals?.flows?.[reverseFlowKey];
    const gridFlow = this.controller.actuals?.flows?.[gridFlowKey] || this.controller.actuals?.flows?.[reverseFlowKey];
    const actualPower = gridFlow?.power_kw ?? 0;
    const maxPower = rootNode.data.max_power_kw;
    const directionArrow = actualPower > 0 ? (isExporting ? "\u2191 " : "\u2193 ") : "";
    const labelText = maxPower
      ? `${directionArrow}${actualPower.toFixed(1)} / ${maxPower} kW`
      : `${directionArrow}${actualPower.toFixed(1)} kW`;
    const gridEdgeColor = this.getEdgeColorForPower(Math.abs(actualPower), maxPower);

    if (this.controller.isHorizontal) {
      const nodeLeftEdge = rootNode.x - this.controller.nodeWidth / 2;
      const gridLineHeight = this.controller.visibleEdgeHorizontal + 32;
      const circleX = nodeLeftEdge - gridLineHeight + circleRadius;
      const lineEndX = nodeLeftEdge;

      gridGroup
        .append("line")
        .attr("x1", circleX)
        .attr("y1", rootNode.y)
        .attr("x2", lineEndX)
        .attr("y2", rootNode.y)
        .attr("stroke", gridEdgeColor)
        .attr("stroke-width", strokeWidth);

      this.renderGridSourceCircle(gridGroup, circleX, rootNode.y, circleRadius);
      this.controller.particleEngine.storeGridLinePath(circleX, rootNode.y, lineEndX, rootNode.y, rootNode.data.id);

      const labelX = (circleX + lineEndX) / 2;
      const labelY = rootNode.y - 12;
      this.renderEdgeLabel(gridGroup, labelX, labelY, labelText);
    } else {
      const nodeTopEdge = rootNode.y - this.controller.nodeHeight / 2;
      const gridLineHeight = this.controller.visibleEdgeVertical + 32;
      const circleY = nodeTopEdge - gridLineHeight + circleRadius;
      const lineEndY = nodeTopEdge;

      gridGroup
        .append("line")
        .attr("x1", rootNode.x)
        .attr("y1", circleY)
        .attr("x2", rootNode.x)
        .attr("y2", lineEndY)
        .attr("stroke", gridEdgeColor)
        .attr("stroke-width", strokeWidth);

      this.renderGridSourceCircle(gridGroup, rootNode.x, circleY, circleRadius);
      this.controller.particleEngine.storeGridLinePath(rootNode.x, circleY, rootNode.x, lineEndY, rootNode.data.id);

      const labelX = rootNode.x + 8 + (labelText.length * 6) / 2;
      const labelY = (circleY + lineEndY) / 2;
      this.renderEdgeLabel(gridGroup, labelX, labelY, labelText);
    }
  }

  renderGridSourceCircle(group, cx, cy, radius) {
    group
      .append("circle")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("r", radius)
      .attr("fill", this.getNodeStrokeColor("grid_connection"))
      .attr("stroke", "none")
      .style("filter", "drop-shadow(0 2px 4px rgba(0,0,0,0.15))");

    const iconGroup = group.append("g").attr("transform", `translate(${cx}, ${cy})`);
    const iconColor = this.controller.isDarkMode ? "#dbeafe" : "#ffffff";

    iconGroup
      .append("path")
      .attr("d", "M0,-9 L-6,2 L-4,2 L-4,9 L-2,9 L-2,4 L2,4 L2,9 L4,9 L4,2 L6,2 Z M-8,2 L-4,2 M4,2 L8,2")
      .attr("fill", "none")
      .attr("stroke", iconColor)
      .attr("stroke-width", 1.5)
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round");
  }

  renderEdges(hierarchy) {
    const links = hierarchy.links();
    const edgesGroup = this.controller.mainGroup.append("g").attr("class", "edges");

    edgesGroup
      .selectAll(".edge")
      .data(links)
      .enter()
      .append("path")
      .attr("class", "edge")
      .attr("d", (d) => this.createEdgePath(d))
      .attr("fill", "none")
      .attr("stroke", (d) => this.getEdgeColorForLink(d))
      .attr("stroke-width", (d) => this.getEdgeWidth(d.target.data.max_power_kw || d.source.data.max_power_kw))
      .each((d) => {
        const forwardKey = `${d.source.data.id}->${d.target.data.id}`;
        const reverseKey = `${d.target.data.id}->${d.source.data.id}`;
        const isConnector = d.target.data.type === "connector";
        this.controller.particleEngine.storeEdgePath(forwardKey, d.source, d.target, isConnector, false);
        this.controller.particleEngine.storeEdgePath(reverseKey, d.source, d.target, isConnector, true);
      });
  }

  getFlowForLink(d) {
    const forwardKey = `${d.source.data.id}->${d.target.data.id}`;
    const reverseKey = `${d.target.data.id}->${d.source.data.id}`;

    const forwardFlow = this.controller.actuals?.flows?.[forwardKey];
    if (forwardFlow) return { flow: forwardFlow, reversed: false };

    const reverseFlow = this.controller.actuals?.flows?.[reverseKey];
    if (reverseFlow) return { flow: reverseFlow, reversed: true };

    return null;
  }

  getUtilizationColor(utilization) {
    if (utilization < 0.3) return "#22c55e";
    if (utilization < 0.6) return "#eab308";
    if (utilization < 0.85) return "#f97316";
    return "#ef4444";
  }

  createEdgePath(d) {
    const isConnector = d.target.data.type === "connector";

    if (this.controller.isHorizontal) {
      const sourceX = d.source.x + this.controller.nodeWidth / 2;
      const targetX = isConnector ? d.target.x - 16 : d.target.x - this.controller.nodeWidth / 2;
      const midX = (sourceX + targetX) / 2;

      return `M${sourceX},${d.source.y}
              C${midX},${d.source.y}
               ${midX},${d.target.y}
               ${targetX},${d.target.y}`;
    } else {
      const sourceY = d.source.y + this.controller.nodeHeight / 2;
      const targetY = isConnector ? d.target.y - 16 : d.target.y - this.controller.nodeHeight / 2;
      const midY = (sourceY + targetY) / 2;

      return `M${d.source.x},${sourceY}
              C${d.source.x},${midY}
               ${d.target.x},${midY}
               ${d.target.x},${targetY}`;
    }
  }

  renderEdgeLabels(hierarchy) {
    const links = hierarchy.links();
    const labelsGroup = this.controller.mainGroup.append("g").attr("class", "edge-labels");

    const linksWithLabels = links.filter((d) => {
      const maxPower = d.target.data.max_power_kw;
      if (!maxPower) return true;
      const labelText = this.getEdgeLabelText(d);
      return labelText !== "0 kW" && labelText !== "0.0 kW";
    });

    linksWithLabels.forEach((d) => {
      const x = this.getEdgeLabelX(d);
      const y = this.getEdgeLabelY(d);
      const text = this.getEdgeLabelText(d);
      const background = this.getEdgeLabelBackground(d);
      const textColor = this.getEdgeLabelTextColor(d);
      this.renderEdgeLabel(labelsGroup, x, y, text, { background, textColor });
    });
  }

  renderEdgeLabel(group, x, y, text, options = {}) {
    const background = options.background || this.getLabelBackgroundColor();
    const textColor = options.textColor || this.getLabelTextColor();
    const textWidth = text.length * 6;

    group
      .append("rect")
      .attr("x", x - textWidth / 2 - 4)
      .attr("y", y - 10)
      .attr("width", textWidth + 8)
      .attr("height", 20)
      .attr("fill", background)
      .attr("stroke", this.getLabelBorderColor())
      .attr("stroke-width", 1)
      .attr("rx", 4);

    group
      .append("text")
      .attr("class", "edge-label")
      .attr("x", x)
      .attr("y", y)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", textColor)
      .attr("font-size", "11px")
      .attr("font-weight", "600")
      .text(text);
  }

  getEdgeLabelText(d) {
    const result = this.getFlowForLink(d);
    const maxPower = d.target.data.max_power_kw;
    const actualPower = result?.flow?.power_kw ?? 0;

    return maxPower ? `${actualPower.toFixed(1)} / ${maxPower} kW` : `${actualPower.toFixed(1)} kW`;
  }

  getEdgeLabelBackground(d) {
    const result = this.getFlowForLink(d);

    if (result && result.flow.power_kw) {
      const color = result.reversed ? "#f59e0b" : "#3b82f6";
      return this.controller.isDarkMode ? this.hexToRgba(color, 0.3) : this.hexToRgba(color, 0.15);
    }
    return this.getLabelBackgroundColor();
  }

  getEdgeLabelTextColor(d) {
    const result = this.getFlowForLink(d);

    if (result && result.flow.power_kw) {
      return this.controller.isDarkMode ? "#ffffff" : "#1e293b";
    }
    return this.getLabelTextColor();
  }

  hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getEdgeLabelX(d) {
    if (this.controller.isHorizontal) {
      const sourceX = d.source.x + this.controller.nodeWidth / 2;
      const isConnector = d.target.data.type === "connector";
      const targetX = isConnector ? d.target.x - 16 : d.target.x - this.controller.nodeWidth / 2;
      return (sourceX + targetX) / 2;
    } else {
      const midX = (d.source.x + d.target.x) / 2;
      if (d.source.x === d.target.x) {
        return midX + 8;
      }
      return midX;
    }
  }

  getEdgeLabelY(d) {
    if (this.controller.isHorizontal) {
      const midY = (d.source.y + d.target.y) / 2;
      if (d.source.y === d.target.y) {
        return midY - 12;
      }
      return midY;
    } else {
      const sourceY = d.source.y + this.controller.nodeHeight / 2;
      const isConnector = d.target.data.type === "connector";
      const targetY = isConnector ? d.target.y - 16 : d.target.y - this.controller.nodeHeight / 2;
      return (sourceY + targetY) / 2;
    }
  }

  getEdgeWidth(powerKw) {
    if (!powerKw) return 2;
    const minWidth = 2;
    const maxWidth = 18;
    const maxPower = 200;

    const normalized = Math.min(powerKw / maxPower, 1);
    return minWidth + normalized * (maxWidth - minWidth);
  }

  renderNodes(hierarchy) {
    const nodes = hierarchy.descendants();
    const nodesGroup = this.controller.mainGroup.append("g").attr("class", "nodes");

    const boxNodes = nodes.filter((d) => d.data.type !== "connector" && d.data.type !== "add_junction_ghost");
    const ghostNodes = nodes.filter((d) => d.data.type === "add_junction_ghost");
    const connectorNodes = nodes.filter((d) => d.data.type === "connector");

    const boxGroups = nodesGroup
      .selectAll(".node-box")
      .data(boxNodes)
      .enter()
      .append("g")
      .attr("class", "node node-box")
      .attr(
        "transform",
        (d) => `translate(${d.x - this.controller.nodeWidth / 2}, ${d.y - this.controller.nodeHeight / 2})`,
      )
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) => this.showTooltip(event, d))
      .on("mouseleave", () => this.hideTooltip())
      .on("mousemove", (event) => this.moveTooltip(event));

    boxGroups
      .append("rect")
      .attr("width", this.controller.nodeWidth)
      .attr("height", this.controller.nodeHeight)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("fill", (d) => this.getNodeColor(d.data.type))
      .attr("stroke", (d) => this.getNodeStrokeColor(d.data.type))
      .attr("stroke-width", 2)
      .style("filter", "drop-shadow(0 2px 4px rgba(0,0,0,0.1))");

    boxGroups
      .append("path")
      .attr("d", (d) => this.getNodeIcon(d.data.type))
      .attr("fill", "none")
      .attr("stroke", (d) => this.getNodeStrokeColor(d.data.type))
      .attr("stroke-width", 1.5)
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("transform", `translate(18, ${this.controller.nodeHeight / 2}) scale(0.9)`);

    boxGroups
      .append("text")
      .attr("x", 36)
      .attr("y", this.controller.nodeHeight / 2)
      .attr("text-anchor", "start")
      .attr("dominant-baseline", "middle")
      .attr("fill", this.getNodeTextColor())
      .attr("font-size", "11px")
      .attr("font-weight", "500")
      .text((d) => this.truncateText(this.formatNodeLabel(d.data), 14));

    if (ghostNodes.length > 0) {
      this.renderGhostNodes(nodesGroup, ghostNodes);
    }

    const collapseBtnX = this.controller.isHorizontal ? this.controller.nodeWidth + 10 : this.controller.nodeWidth / 2;
    const collapseBtnY = this.controller.isHorizontal
      ? this.controller.nodeHeight / 2
      : this.controller.nodeHeight + 10;

    boxGroups
      .filter((d) => this.nodeHasChildren(d.data.id))
      .append("circle")
      .attr("cx", collapseBtnX)
      .attr("cy", collapseBtnY)
      .attr("r", 10)
      .attr("fill", this.getCollapseButtonColor())
      .attr("stroke", this.getEdgeColor())
      .attr("stroke-width", 1)
      .attr("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        this.toggleCollapse(d);
      });

    boxGroups
      .filter((d) => this.nodeHasChildren(d.data.id))
      .append("text")
      .attr("x", collapseBtnX)
      .attr("y", collapseBtnY + 4)
      .attr("text-anchor", "middle")
      .attr("fill", this.getSecondaryTextColor())
      .attr("font-size", "12px")
      .attr("font-weight", "bold")
      .style("pointer-events", "none")
      .text((d) => (this.collapsedNodes.has(d.data.id) ? "+" : "\u2212"));

    const connectorGroups = nodesGroup
      .selectAll(".node-connector")
      .data(connectorNodes)
      .enter()
      .append("g")
      .attr("class", "node node-connector")
      .attr("transform", (d) => `translate(${d.x}, ${d.y})`)
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) => this.showTooltip(event, d))
      .on("mouseleave", () => this.hideTooltip())
      .on("mousemove", (event) => this.moveTooltip(event));

    connectorGroups
      .append("circle")
      .attr("r", 16)
      .attr("fill", (d) => this.getConnectorFillColor(d.data))
      .attr("stroke", "none")
      .attr("class", (d) => (this.isConnectorDeliveringPower(d.data) ? "power-pulse" : ""))
      .style("filter", "drop-shadow(0 2px 4px rgba(0,0,0,0.15))");

    this.renderType2Icon(connectorGroups);
    this.renderEnergyProgressArc(connectorGroups);
    this.renderConnectorStatusLabel(connectorGroups);
  }

  renderGhostNodes(nodesGroup, ghostNodes) {
    const isDark = this.controller.isDarkMode;
    const w = this.controller.nodeWidth;
    const h = this.controller.nodeHeight;

    const ghostGroups = nodesGroup
      .selectAll(".node-ghost")
      .data(ghostNodes)
      .enter()
      .append("g")
      .attr("class", "node node-ghost")
      .attr("transform", (d) => `translate(${d.x - w / 2}, ${d.y - h / 2})`)
      .style("cursor", "pointer");

    ghostGroups
      .append("rect")
      .attr("width", w)
      .attr("height", h)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("fill", isDark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.06)")
      .attr("stroke", "var(--color-action-fill, #3b82f6)")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "6,4")
      .attr("stroke-dashoffset", 0)
      .each(function () {
        const rect = this;
        function marchingAnts() {
          d3.select(rect)
            .transition()
            .duration(800)
            .ease(d3.easeLinear)
            .attr("stroke-dashoffset", -20)
            .on("end", function () {
              d3.select(this).attr("stroke-dashoffset", 0);
              marchingAnts();
            });
        }
        marchingAnts();
      });

    ghostGroups
      .append("text")
      .attr("x", w / 2)
      .attr("y", h / 2 + 1)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "var(--color-action-fill, #3b82f6)")
      .attr("font-size", "24px")
      .attr("font-weight", "400")
      .attr("opacity", 0.7)
      .style("pointer-events", "none")
      .text("+");
  }

  renderConnectorStatusLabel(connectorGroups) {
    const connectorsWithStatus = connectorGroups.filter((d) => {
      const state = this.controller.actuals?.node_states?.[d.data.id];
      return state?.status;
    });

    connectorsWithStatus
      .append("text")
      .attr("x", this.controller.isHorizontal ? 32 : 0)
      .attr("y", (d) => {
        const state = this.controller.actuals?.node_states?.[d.data.id];
        const hasEnergyTarget = state?.energy_target_kwh > 0;
        if (this.controller.isHorizontal) {
          return hasEnergyTarget ? 12 : 0;
        }
        return hasEnergyTarget ? 50 : 38;
      })
      .attr("text-anchor", this.controller.isHorizontal ? "start" : "middle")
      .attr("fill", (d) => this.getConnectorFillColor(d.data))
      .attr("font-size", "9px")
      .attr("font-weight", "600")
      .text((d) => {
        const state = this.controller.actuals?.node_states?.[d.data.id];
        const status = state?.status;
        return this.controller.i18nConnectorStatesValue?.[status] || status;
      });
  }

  getConnectorFillColor(nodeData) {
    const state = this.controller.actuals?.node_states?.[nodeData.id];
    if (state?.session_active) {
      return state.smart ? this.controller.chargingColors.smart : this.controller.chargingColors.direct;
    }
    return this.controller.isDarkMode ? "#64748b" : "#94a3b8";
  }

  isConnectorDeliveringPower(nodeData) {
    const state = this.controller.actuals?.node_states?.[nodeData.id];
    return state?.power_kw > 0;
  }

  renderEnergyProgressArc(connectorGroups) {
    const arcGenerator = d3
      .arc()
      .innerRadius(20)
      .outerRadius(24)
      .startAngle(-Math.PI / 2);

    const connectorsWithTarget = connectorGroups.filter((d) => {
      const state = this.controller.actuals?.node_states?.[d.data.id];
      return state?.energy_target_kwh > 0;
    });

    connectorsWithTarget
      .append("path")
      .attr("d", (d) => {
        const state = this.controller.actuals?.node_states?.[d.data.id];
        const delivered = state?.energy_delivered_kwh || 0;
        const target = state?.energy_target_kwh || 1;
        const progress = Math.min(delivered / target, 1);
        const endAngle = -Math.PI / 2 + progress * 2 * Math.PI;
        return arcGenerator({ endAngle });
      })
      .attr("fill", (d) => this.getConnectorFillColor(d.data))
      .attr("opacity", 0.9);

    connectorsWithTarget
      .append("text")
      .attr("x", this.controller.isHorizontal ? 32 : 0)
      .attr("y", this.controller.isHorizontal ? 0 : 38)
      .attr("text-anchor", this.controller.isHorizontal ? "start" : "middle")
      .attr("dominant-baseline", this.controller.isHorizontal ? "middle" : "auto")
      .attr("fill", this.controller.isDarkMode ? "#9ca3af" : "#6b7280")
      .attr("font-size", "9px")
      .text((d) => {
        const state = this.controller.actuals?.node_states?.[d.data.id];
        const delivered = state?.energy_delivered_kwh || 0;
        const target = state?.energy_target_kwh || 0;
        return `${delivered.toFixed(1)} / ${target.toFixed(1)} kWh`;
      });
  }

  isConnectorCharging(nodeData) {
    const state = this.controller.actuals?.node_states?.[nodeData.id];
    return state?.session_active === true;
  }

  formatNodeLabel(nodeData) {
    const name = nodeData.name || "";
    const label = nodeData.type === "charge_point" ? name.replace(/^CP\s+/i, "") : name;
    return nodeData.orphan ? `\u26A0 ${label}` : label;
  }

  showTooltip(event, d) {
    if (!this.controller.hasTooltipTarget) return;
    if (this.controller.topologyEditor?.enabled) return;

    const content = this.buildTooltipContent(d.data);
    this.controller.tooltipTarget.innerHTML = content;
    this.controller.tooltipTarget.classList.remove("hidden");
    this.moveTooltip(event);
  }

  hideTooltip() {
    if (!this.controller.hasTooltipTarget) return;
    this.controller.tooltipTarget.classList.add("hidden");
  }

  moveTooltip(event) {
    if (!this.controller.hasTooltipTarget) return;

    const container = this.controller.hasContainerTarget ? this.controller.containerTarget : this.controller.element;
    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left + 15;
    const y = event.clientY - rect.top + 15;

    this.controller.tooltipTarget.style.left = `${x}px`;
    this.controller.tooltipTarget.style.top = `${y}px`;
  }

  buildTooltipContent(nodeData) {
    const lines = [];
    const state = this.controller.actuals?.node_states?.[nodeData.id];

    const defaultTypeLabels = {
      grid_connection: "Grid Connection",
      junction: "Charging Site",
      charge_point: "Charge Point",
      connector: "Connector",
      pv: "Solar PV",
      solar_inverter: "Solar Inverter",
      solar_array: "Solar Array",
      battery: "Battery",
      load: "Load",
      other_loads: "Other Loads",
    };
    const typeLabels = this.controller.i18nNodeTypesValue || defaultTypeLabels;

    const displayName = nodeData.identifier || this.formatNodeLabel(nodeData);
    lines.push(`<div class="font-semibold">${displayName}</div>`);
    lines.push(
      `<div class="text-xs text-gray-500 dark:text-gray-400">${typeLabels[nodeData.type] || nodeData.type}</div>`,
    );

    if (nodeData.orphan) {
      lines.push(
        `<div class="mt-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded text-amber-700 dark:text-amber-300 text-xs font-medium">\u26A0 Not in topology \u2014 needs to be assigned</div>`,
      );
    }

    if (nodeData.address) {
      lines.push(`<div class="text-xs text-gray-500 dark:text-gray-400">${nodeData.address}</div>`);
    }

    const chargingLabel = this.controller.i18nChargingValue || "Charging";
    if (state?.session_active) {
      const liveInfo = [];
      if (state.power_kw) {
        liveInfo.push(`${state.power_kw.toFixed(1)} kW`);
      }
      if (state.soc_percent !== undefined && state.soc_percent !== null) {
        const socLabel = this.controller.i18nSocValue || "SoC";
        liveInfo.push(`${socLabel}: ${Math.round(state.soc_percent)}%`);
      }
      if (liveInfo.length > 0) {
        lines.push(
          `<div class="mt-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300 text-xs font-medium">\u26A1 ${chargingLabel}: ${liveInfo.join(" \u00B7 ")}</div>`,
        );
      } else {
        lines.push(
          `<div class="mt-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300 text-xs font-medium">\u26A1 ${chargingLabel}</div>`,
        );
      }
    }

    const threePhaseLabel = this.controller.i18nThreePhaseValue || "3-phase";
    const onePhaseLabel = this.controller.i18nOnePhaseValue || "1-phase";
    const specs = [];
    const feedIn = nodeData.min_power_kw != null ? Math.abs(nodeData.min_power_kw) : null;
    const consumption = nodeData.max_power_kw;
    if (feedIn && consumption && feedIn === consumption) {
      specs.push(`\u2195${consumption} kW`);
    } else if (feedIn || consumption) {
      if (feedIn) specs.push(`\u2191${feedIn} kW`);
      if (consumption) specs.push(`\u2193${consumption} kW`);
    }
    if (nodeData.max_current_amp) {
      specs.push(`${nodeData.max_current_amp} A`);
    }
    if (nodeData.number_of_phases) {
      specs.push(nodeData.number_of_phases === 3 ? threePhaseLabel : onePhaseLabel);
    }
    if (specs.length > 0) {
      lines.push(`<div class="mt-1 font-medium">${specs.join(" \u00B7 ")}</div>`);
    }

    if (nodeData.model) {
      lines.push(`<div class="text-xs text-gray-500 dark:text-gray-400 mt-1">${nodeData.model}</div>`);
    }

    if (nodeData.type === "solar_array" && nodeData.panel_count && nodeData.wp_per_panel) {
      lines.push(this.buildSolarArrayTooltip(nodeData, state));
    }

    return lines.join("");
  }

  buildSolarArrayTooltip(nodeData, state) {
    const lines = [];
    const panelCount = nodeData.panel_count || 0;
    const wpPerPanel = nodeData.wp_per_panel || 0;
    const totalWp = panelCount * wpPerPanel;
    const totalKwp = (totalWp / 1000).toFixed(1);
    const azimuth = nodeData.azimuth || 0;
    const declination = nodeData.declination || 0;
    const directionName = this.getAzimuthDirectionName(azimuth);

    const powerKw = state?.power_kw ?? 0;

    lines.push(`<div class="mt-2 border-t border-gray-200 dark:border-gray-600 pt-2">`);

    lines.push(`<div class="flex items-start gap-3">`);

    const arrowRotation = azimuth;
    lines.push(`<div class="flex-shrink-0">`);
    const compassLabels = this.controller.i18nCompassLabelsValue || { n: "N", e: "E", s: "S", w: "W" };
    lines.push(`<svg width="60" height="60" viewBox="0 0 60 60" class="text-gray-400 dark:text-gray-500">`);
    lines.push(`<circle cx="30" cy="30" r="28" fill="none" stroke="currentColor" stroke-width="1.5"/>`);
    lines.push(
      `<text x="30" y="10" text-anchor="middle" font-size="8" fill="currentColor" font-weight="bold">${compassLabels.n}</text>`,
    );
    lines.push(`<text x="54" y="33" text-anchor="middle" font-size="8" fill="currentColor">${compassLabels.e}</text>`);
    lines.push(`<text x="30" y="56" text-anchor="middle" font-size="8" fill="currentColor">${compassLabels.s}</text>`);
    lines.push(`<text x="6" y="33" text-anchor="middle" font-size="8" fill="currentColor">${compassLabels.w}</text>`);
    lines.push(`<g transform="rotate(${arrowRotation}, 30, 30)">`);
    lines.push(`<line x1="30" y1="30" x2="30" y2="14" stroke="#f59e0b" stroke-width="3" stroke-linecap="round"/>`);
    lines.push(`<polygon points="30,10 26,18 34,18" fill="#f59e0b"/>`);
    lines.push(`</g>`);
    lines.push(`<circle cx="30" cy="30" r="3" fill="#f59e0b"/>`);
    lines.push(`</svg>`);
    lines.push(`</div>`);

    const directionLabel = this.controller.i18nDirectionValue || "Direction";
    const tiltLabel = this.controller.i18nTiltValue || "Tilt";
    const panelsLabel = this.controller.i18nPanelsValue || "Panels";
    const powerLabel = this.controller.i18nPowerValue || "Power";
    const currentLabel = this.controller.i18nCurrentValue || "Current";

    lines.push(`<div class="flex-1 text-xs space-y-1">`);
    lines.push(
      `<div class="flex justify-between"><span class="text-gray-500 dark:text-gray-400">${directionLabel}</span><span class="font-medium">${directionName} (${azimuth}\u00B0)</span></div>`,
    );
    lines.push(
      `<div class="flex justify-between"><span class="text-gray-500 dark:text-gray-400">${tiltLabel}</span><span>${declination}\u00B0</span></div>`,
    );
    lines.push(`<div class="border-t border-gray-200 dark:border-gray-600 my-1 pt-1"></div>`);
    lines.push(
      `<div class="flex justify-between"><span class="text-gray-500 dark:text-gray-400">${panelsLabel}</span><span>${panelCount}\u00D7 ${wpPerPanel}Wp</span></div>`,
    );
    lines.push(
      `<div class="flex justify-between"><span class="text-gray-500 dark:text-gray-400">${powerLabel}</span><span class="font-medium">${totalKwp} kWp</span></div>`,
    );
    if (powerKw > 0) {
      lines.push(
        `<div class="flex justify-between text-green-600 dark:text-green-400"><span>${currentLabel}</span><span class="font-medium">${powerKw.toFixed(1)} kW</span></div>`,
      );
    }
    lines.push(`</div>`);

    lines.push(`</div>`);
    lines.push(`</div>`);

    return lines.join("");
  }

  getNodeColor(type) {
    const styles = getComputedStyle(document.documentElement);
    const yellowLight = styles.getPropertyValue("--color-yellow-100").trim() || "#fef1d0";
    const velvetLight = styles.getPropertyValue("--color-velvet-100").trim() || "#fcdedf";
    const grayLight = this.controller.isDarkMode ? "#1e293b" : "#f1f5f9";

    if (this.controller.isDarkMode) {
      const darkColors = {
        pv: "#422006",
        solar_inverter: "#422006",
        solar_array: "#451a03",
        other_loads: "#4a1532",
      };
      return darkColors[type] || "#1e293b";
    }
    const colors = {
      pv: yellowLight,
      solar_inverter: yellowLight,
      solar_array: yellowLight,
      other_loads: velvetLight,
    };
    return colors[type] || grayLight;
  }

  getNodeStrokeColor(type) {
    const styles = getComputedStyle(document.documentElement);
    const yellow = styles.getPropertyValue("--color-energy-fill-solar").trim() || "#fcbf2f";
    const velvet = styles.getPropertyValue("--color-velvet").trim() || "#f15f8a";
    const gray = this.controller.isDarkMode ? "#94a3b8" : "#64748b";

    const colors = {
      pv: yellow,
      solar_inverter: yellow,
      solar_array: yellow,
      other_loads: velvet,
    };
    return colors[type] || gray;
  }

  getLabelBackgroundColor() {
    return this.controller.isDarkMode ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.8)";
  }

  getLabelBorderColor() {
    return this.controller.isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
  }

  getLabelTextColor() {
    return this.controller.isDarkMode ? "#e2e8f0" : "#1e293b";
  }

  getNodeTextColor() {
    return this.controller.isDarkMode ? "#e2e8f0" : "#1e293b";
  }

  getSecondaryTextColor() {
    return this.controller.isDarkMode ? "#94a3b8" : "#64748b";
  }

  getEdgeColor() {
    return this.controller.isDarkMode ? "#334155" : "#cbd5e1";
  }

  getEdgeColorForLink(d) {
    const flowData = this.getFlowForLink(d);
    if (!flowData) return this.getEdgeColor();

    if (this.isSolarFlow(d) && flowData.flow.utilization != null) {
      return this.getSolarEdgeColor(flowData.flow.utilization);
    }

    const maxPower = d.target.data.max_power_kw;
    return this.getEdgeColorForPower(Math.abs(flowData.flow.power_kw), maxPower);
  }

  isSolarFlow(d) {
    return (d.source.data.id || "").includes("solar") || (d.target.data.id || "").includes("solar");
  }

  getSolarEdgeColor(utilization) {
    if (utilization <= 0) return this.getEdgeColor();
    const t = Math.min(utilization * 2, 1);
    return this.interpolateColor(this.getEdgeColorRgb(), this.getSolarColorRgb(), t);
  }

  getSolarColorRgb() {
    const hex =
      getComputedStyle(document.documentElement).getPropertyValue("--color-energy-fill-solar").trim() || "#fcbf2f";
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  getEdgeColorForPower(actualPower, maxPower) {
    if (!maxPower) return this.getEdgeColor();

    const utilization = actualPower / maxPower;
    const blueRgb = [55, 144, 255];
    const redRgb = [194, 0, 0];
    const overloadGrace = 1.05;
    const overloadFull = 1.10;

    if (utilization <= 0) return this.getEdgeColor();

    if (utilization <= overloadGrace) {
      const t = Math.min(utilization / overloadGrace, 1);
      return this.interpolateColor(this.getEdgeColorRgb(), blueRgb, t);
    }

    if (utilization <= overloadFull) {
      const t = (utilization - overloadGrace) / (overloadFull - overloadGrace);
      return this.interpolateColor(blueRgb, redRgb, t);
    }

    return this.interpolateColor(blueRgb, redRgb, 1);
  }

  getEdgeColorRgb() {
    return this.controller.isDarkMode ? [51, 65, 85] : [203, 213, 225];
  }

  interpolateColor(from, to, t) {
    const r = Math.round(from[0] + (to[0] - from[0]) * t);
    const g = Math.round(from[1] + (to[1] - from[1]) * t);
    const b = Math.round(from[2] + (to[2] - from[2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }

  getCollapseButtonColor() {
    return this.controller.isDarkMode ? "#334155" : "#e2e8f0";
  }

  renderType2Icon(connectorGroups) {
    const iconGroup = connectorGroups.append("g").attr("class", "type2-icon");

    const socketFill = this.controller.isDarkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.7)";
    const pinColor = this.controller.isDarkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.25)";

    iconGroup
      .append("path")
      .attr("d", "M-7,-11 Q-11,-11 -11,-7 L-11,5 Q-11,11 0,11 Q11,11 11,5 L11,-7 Q11,-11 7,-11 Z")
      .attr("fill", socketFill)
      .attr("stroke", "none");

    iconGroup.append("circle").attr("cx", -3.5).attr("cy", -6).attr("r", 1.3).attr("fill", pinColor);
    iconGroup.append("circle").attr("cx", 3.5).attr("cy", -6).attr("r", 1.3).attr("fill", pinColor);

    iconGroup.append("circle").attr("cx", -6).attr("cy", 0).attr("r", 2).attr("fill", pinColor);
    iconGroup.append("circle").attr("cx", 0).attr("cy", 0).attr("r", 2).attr("fill", pinColor);
    iconGroup.append("circle").attr("cx", 6).attr("cy", 0).attr("r", 2).attr("fill", pinColor);

    iconGroup.append("circle").attr("cx", -3.5).attr("cy", 6).attr("r", 2).attr("fill", pinColor);
    iconGroup.append("circle").attr("cx", 3.5).attr("cy", 6).attr("r", 2).attr("fill", pinColor);
  }

  getNodeIcon(type) {
    const icons = {
      grid_connection: "M-8,-6 L8,-6 L8,6 L-8,6 Z M-5,-3 L-5,3 M0,-3 L0,3 M5,-3 L5,3",
      junction: "M-4,-8 L4,-8 L4,8 L-4,8 Z M-2,-5 L-2,0 L2,0 L2,-5 M0,0 L0,5",
      charge_point: "M-6,-8 L6,-8 L6,8 L-6,8 Z M-3,-5 L-3,2 L3,2 L3,-5 M0,2 L0,5",
      pv: "M-8,-4 L8,-4 L8,4 L-8,4 Z M-6,-2 L-2,-2 L-2,2 L-6,2 Z M2,-2 L6,-2 L6,2 L2,2 Z",
      solar_inverter:
        "M-2,-5 L2,-5 L5,-2 L5,2 L2,5 L-2,5 L-5,2 L-5,-2 Z M0,-7 L0,-9 M0,7 L0,9 M-7,0 L-9,0 M7,0 L9,0 M-5,-5 L-7,-7 M5,-5 L7,-7 M-5,5 L-7,7 M5,5 L7,7",
      solar_array: "M-8,-4 L8,-4 L8,4 L-8,4 Z M-4,-4 L-4,4 M0,-4 L0,4 M4,-4 L4,4",
      battery: "M-7,-4 L7,-4 L7,4 L-7,4 Z M7,-2 L9,-2 L9,2 L7,2 Z M-5,-2 L-5,2 M-2,-2 L-2,2 M1,-2 L1,2",
      load: "M0,-8 L4,0 L0,8 L-4,0 Z",
      other_loads: "M0,-8 L-6,0 L-3,0 L-3,8 L3,8 L3,0 L6,0 Z",
    };
    return icons[type] || "";
  }

  truncateText(text, maxLength) {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength - 1) + "\u2026" : text;
  }

  getAzimuthDirection(azimuth) {
    if (azimuth === undefined || azimuth === null) return "?";
    const dirs = this.controller.i18nDirectionsValue || {
      n: "N",
      ne: "NE",
      e: "E",
      se: "SE",
      s: "S",
      sw: "SW",
      w: "W",
      nw: "NW",
    };
    const normalized = azimuth % 360;
    if (normalized === 0 || normalized === 360) return dirs.n;
    if (normalized > 0 && normalized < 90) return dirs.ne;
    if (normalized === 90) return dirs.e;
    if (normalized > 90 && normalized < 180) return dirs.se;
    if (normalized === 180) return dirs.s;
    if (normalized > 180 && normalized < 270) return dirs.sw;
    if (normalized === 270) return dirs.w;
    if (normalized > 270 && normalized < 360) return dirs.nw;
    return "?";
  }

  getAzimuthDirectionName(azimuth) {
    if (azimuth === undefined || azimuth === null) return "?";
    const dirs = this.controller.i18nDirectionNamesValue || {
      n: "North",
      ne: "Northeast",
      e: "East",
      se: "Southeast",
      s: "South",
      sw: "Southwest",
      w: "West",
      nw: "Northwest",
    };
    const normalized = azimuth % 360;
    if (normalized === 0 || normalized === 360) return dirs.n;
    if (normalized > 0 && normalized < 90) return dirs.ne;
    if (normalized === 90) return dirs.e;
    if (normalized > 90 && normalized < 180) return dirs.se;
    if (normalized === 180) return dirs.s;
    if (normalized > 180 && normalized < 270) return dirs.sw;
    if (normalized === 270) return dirs.w;
    if (normalized > 270 && normalized < 360) return dirs.nw;
    return "?";
  }

  nodeHasChildren(nodeId) {
    return this.controller.nodes.some((n) => n.parent === nodeId);
  }

  toggleCollapse(d) {
    const nodeId = d.data.id;
    if (this.collapsedNodes.has(nodeId)) {
      this.collapsedNodes.delete(nodeId);
    } else {
      this.collapsedNodes.add(nodeId);
    }
    localStorage.setItem("digitalTwinCollapsed", JSON.stringify([...this.collapsedNodes]));
    this.controller.particleEngine.reset();
    this.render();
    this.controller.fitToView();
  }
}
